import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import AssistantPanelDropdownMenu from "components/AssistantPanel/components/DropdownMenu";
import { Dots, Expand, External, Pin } from "components/icons/generated";
import { IconComponent } from "types/Icon";

import useAssistantPanelStore from "../../useStore";
import { AssistantPanelVariant } from "../../types";
import useAssistantPanelAnalytics from "../../../analytics/useAnalytics";

const VARIANT_OPTIONS: { variant: AssistantPanelVariant; label: string; icon: IconComponent }[] = [
  { variant: "floating", label: "Floating", icon: External },
  { variant: "fullscreen", label: "Full screen", icon: Expand },
  { variant: "side", label: "Pinned", icon: Pin },
];

const AssistantPanelLayoutHeaderModeDropdown = () => {
  const { variant, pin, unpin, toggleFullscreen } = useAssistantPanelStore(
    useShallow((state) => ({
      variant: state.variant,
      pin: state.pin,
      unpin: state.unpin,
      toggleFullscreen: state.toggleFullscreen,
    }))
  );
  const { trackPanelVariantChanged } = useAssistantPanelAnalytics();

  const handleSelect = useCallback(
    (target: AssistantPanelVariant) => {
      if (target === variant) {
        return;
      }
      if (target === "side") {
        pin();
      } else if (target === "floating") {
        unpin();
      } else {
        toggleFullscreen();
      }
      trackPanelVariantChanged({ variant: target });
    },
    [variant, pin, unpin, toggleFullscreen, trackPanelVariantChanged]
  );

  return (
    <AssistantPanelDropdownMenu
      placement="bottom right"
      triggerComponent={
        <ButtonIcon icon={Dots} variant="ghost">
          Change view
        </ButtonIcon>
      }
    >
      {VARIANT_OPTIONS.map(({ variant: optionVariant, label, icon }) => (
        <DropdownMenuItem
          key={optionVariant}
          active={optionVariant === variant}
          onAction={() => handleSelect(optionVariant)}
        >
          <Box gap="medium" align="center">
            <Icon src={icon} />
            {label}
          </Box>
        </DropdownMenuItem>
      ))}
    </AssistantPanelDropdownMenu>
  );
};

export default AssistantPanelLayoutHeaderModeDropdown;
